import { useEffect, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const PRESENCE_KEY = 'iamhethatis_session_id';
const HEARTBEAT_MS = 30000;

const getSessionId = () => {
  try {
    let sid = localStorage.getItem(PRESENCE_KEY);
    if (!sid) {
      // Lightweight UUID without depending on uuid package
      sid =
        (crypto && crypto.randomUUID && crypto.randomUUID()) ||
        `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(PRESENCE_KEY, sid);
    }
    return sid;
  } catch (_) {
    return `s_${Date.now()}`;
  }
};

/**
 * Anonymous presence tracker — fires a heartbeat on mount, then every 30s
 * while any tab is open. Sends a best-effort beacon on unload so the count
 * drops quickly when users leave.
 *
 * Mount this once at the app root.
 */
export const usePresence = () => {
  const intervalRef = useRef(null);

  useEffect(() => {
    const sid = getSessionId();

    const beat = (path = window.location.pathname) => {
      axios
        .post(`${API}/api/presence/heartbeat`, { session_id: sid, path })
        .catch(() => {});
    };

    // Initial heartbeat + interval
    beat();
    intervalRef.current = setInterval(() => beat(), HEARTBEAT_MS);

    // Tab visibility — pause/resume sensibly
    const onVisibility = () => {
      if (document.visibilityState === 'visible') beat();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Tab close / nav away — best-effort beacon
    const onUnload = () => {
      try {
        const blob = new Blob([JSON.stringify({ session_id: sid })], { type: 'application/json' });
        navigator.sendBeacon?.(`${API}/api/presence/leave`, blob);
      } catch (_) {
        /* ignore */
      }
    };
    window.addEventListener('pagehide', onUnload);
    window.addEventListener('beforeunload', onUnload);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onUnload);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, []);
};

export default usePresence;
